import { Box } from '@mui/material'
import { useCallback, useEffect, useRef } from 'react'

const AUTO_SCROLL_BOTTOM_THRESHOLD_PX = 8

export type TerminalEntry = {
  text: string
  source: 'serial' | 'log'
}

type Props = {
  terminalEntries: TerminalEntry[]
  placeholder: string
}

const CertificateTerminal = ({ terminalEntries, placeholder }: Props) => {
  const terminalRef = useRef<HTMLDivElement | null>(null)
  const shouldAutoScrollRef = useRef(true)
  const isManualScrollRef = useRef(false)
  const manualScrollTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    if (!terminalRef.current || !shouldAutoScrollRef.current) return
    terminalRef.current.scrollTop = terminalRef.current.scrollHeight
  }, [terminalEntries])

  useEffect(
    () => () => {
      if (manualScrollTimeoutRef.current !== null) {
        window.clearTimeout(manualScrollTimeoutRef.current)
      }
    },
    [],
  )

  const markManualScrollIntent = useCallback(() => {
    isManualScrollRef.current = true
    if (manualScrollTimeoutRef.current !== null) {
      window.clearTimeout(manualScrollTimeoutRef.current)
    }
    manualScrollTimeoutRef.current = window.setTimeout(() => {
      isManualScrollRef.current = false
    }, 1000)
  }, [])

  const handleTerminalScroll = useCallback(() => {
    if (!terminalRef.current || !isManualScrollRef.current) return

    const distanceFromBottom =
      terminalRef.current.scrollHeight -
      terminalRef.current.scrollTop -
      terminalRef.current.clientHeight

    shouldAutoScrollRef.current =
      distanceFromBottom <= AUTO_SCROLL_BOTTOM_THRESHOLD_PX
  }, [])

  const renderEntries = () => {
    if (!terminalEntries.length) {
      return placeholder
    }

    return terminalEntries.map((entry, index) => (
      <Box
        key={`${index}-${entry.source}`}
        component="span"
        sx={{
          color: entry.source === 'serial' ? '#7dd3fc' : '#c8facc',
        }}
      >
        {entry.text}
      </Box>
    ))
  }

  return (
    <Box
      data-testid="certificate-terminal"
      ref={terminalRef}
      onScroll={handleTerminalScroll}
      onWheel={markManualScrollIntent}
      onMouseDown={markManualScrollIntent}
      onTouchStart={markManualScrollIntent}
      sx={{
        mt: 3,
        backgroundColor: '#0b0f14',
        color: '#c8facc',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.800',
        p: 2,
        minHeight: 180,
        maxHeight: 280,
        overflowY: 'auto',
        fontFamily: 'Monaco, Menlo, Consolas, "Courier New", monospace',
        fontSize: '0.8rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {renderEntries()}
    </Box>
  )
}

export default CertificateTerminal
